import httpClient from '../plugins/axios'
import { customerTypeMap, TPL_PROD_CODE } from '@/constants/prodCodes'
import i18n from '@/i18n'
import { removeVersionFromEndpoint } from '@/utils/removeVersionFromEndpoint'

function dateToUnix (date) {
  return Math.floor((date.getTime() - date.getTimezoneOffset() * 60000) / 1000)
}

const defaultState = {
  changePlan: {
    status: false,
    prodCode: null
  },
  renewScheme: null,
  policySchemesInfo: {
    policyNumber: null,
    promocode: null,
    info: {
      quoteno: null,
      schemes: null
    }
  },
  motorUpgradeSchemes: {
    promocode: null,
    value: null,
    info: {
      quoteno: null,
      schemes: null
    }
  },
  motorInfoToChange: {
    userInfo: null,
    motorInfo: null
  },
  carLists: {
    shapeList: null,
    plateList: null
  },
  dateOfBirth: null,
  promocode: null,
  promoCampaign: null,
  vehicleValue: null,
  makesAndModels: [],
  makesCar: [],
  isSuccessPromocode: false,
  isCampaignPromoCodeApplied: false,
  isDowngradePossible: false,
  isUpgradePossible: false
}

function saveState (state) {
  localStorage.setItem('qic_renew_motor', JSON.stringify(state))
}

function savedState () {
  let result = {}

  try {
    result = JSON.parse(localStorage.getItem('qic_renew_motor'))
  } catch { }

  return result
}

export default {
  state () {
    return {
      ...defaultState,
      ...savedState()
    }
  },
  getters: {
    odQuoteNumber (state, _, store) {
      const policyInfo = store.renew.policyInfo
      if (policyInfo?.prodCode === TPL_PROD_CODE) return state.motorUpgradeSchemes?.info?.quoteno || ''
      return state.policySchemesInfo?.info?.quoteno || ''
    },
    tplQuoteNumber  (state) {
      return state.policySchemesInfo?.info?.quoteno || ''
    }
  },
  mutations: {
    setPromoCampaign (state, data) {
      state.promoCampaign = data
    },
    setMakesAndModels (state, data) {
      state.makesAndModels = data
    },
    setMakesCar (state, data) {
      state.makesCar = data
    },
    setMotorChangePlan (state, { prodCode, status }) {
      state.changePlan.prodCode = prodCode
      state.changePlan.status = status
      saveState(state)
    },
    setMotorRenewScheme (state, renewScheme) {
      state.renewScheme = renewScheme
      saveState(state)
    },
    setMotorDob (state, dateOfBirth) {
      state.dateOfBirth = dateOfBirth
      saveState(state)
    },
    setMotorQuoteNumber (state, quoteNumber) {
      state.quoteNumber = quoteNumber
      saveState(state)
    },
    setMotorPolicySchemesInfo (state, { info, policyNumber, promocode }) {
      state.policySchemesInfo.info = info
      state.policySchemesInfo.policyNumber = policyNumber
      state.policySchemesInfo.promocode = promocode
      saveState(state)
    },
    clearMotorData (state) {
      state.renewScheme = null
      state.policySchemesInfo = {
        policyNumber: null,
        promocode: null,
        info: {}
      }
      state.motorUpgradeSchemes = {
        promocode: null,
        value: null,
        info: {
          quoteno: null,
          schemes: null
        }
      }
      state.changePlan = {
        prodCode: null,
        status: false
      }
      state.motorInfoToChange = {
        userInfo: null,
        motorInfo: null
      }
      saveState(state)
    },
    setMotorSchemesUpgradeInfo (state, upgradeSchemes) {
      state.motorUpgradeSchemes.info = upgradeSchemes.info
      state.motorUpgradeSchemes.value = upgradeSchemes.value
      saveState(state)
    },
    setCarShapeList (state, list) {
      state.carLists.shapeList = list
      saveState(state)
    },
    setCarPlateList (state, list) {
      state.carLists.plateList = list
      saveState(state)
    },
    setMotorPromocode (state, promocode) {
      state.promocode = promocode
      saveState(state)
    },
    setIsSuccessPromocode (state, isSuccessPromocode) {
      state.isSuccessPromocode = isSuccessPromocode
    },
    setIsCampaignPromoCodeApplied (state, isPromoCampaignApplied) {
      state.isCampaignPromoCodeApplied = isPromoCampaignApplied
    },
    setVehicleValue (state, vehicleValue) {
      state.vehicleValue = vehicleValue
      saveState(state)
    },
    setIsDowngradePossible (state, isDowngradePossible) {
      state.isDowngradePossible = isDowngradePossible
      saveState(state)
    },
    setIsUpgradePossible (state, isUpgradePossible) {
      state.isUpgradePossible = isUpgradePossible
      saveState(state)
    }
  },
  actions: {
    getPromoCampaign ({ commit, state }, vehicleValue) {
      const { locale } = i18n
      const baseURL = removeVersionFromEndpoint(process.env.VUE_APP_API_URL)
      const params = {
        lang: locale,
        policy: state.policySchemesInfo.policyNumber,
        carvalue: vehicleValue
      }
      const queryString = window.location.search
      const urlParams = new URLSearchParams(queryString)
      const UTMs = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content']
      const addUtmToParams = (utm) => {
        const utmData = urlParams.get(utm)
        if (utmData) {
          params[utm] = decodeURIComponent(utmData)
        }
      }
      UTMs.forEach(addUtmToParams)
      return httpClient.get('/promocampaign/senduserdata',
        {
          params,
          baseURL
        })
        .then(({ data }) => {
          const { promoCar } = data
          if (promoCar) {
            commit('setPromoCampaign', promoCar)
          } else {
            commit('setPromoCampaign', null)
          }
        })
        .catch(() => console.warn('No promotional code available'))
    },
    async getMakesAndModels ({ commit }, searchValue) {
      const list = await httpClient.get('/findmakemodel', {
        params: { name: searchValue },
        headers: { 'Qic-Product-Code': 'qarenew' }
      })
        .then(({ data }) => data.list)
      const makeAndModels = list
        .map(({ makeName, modelName, modelCode, makeCode }) => ({
          name: `${makeName} ${modelName}`,
          modelCode,
          makeCode
        }))
      return commit('setMakesAndModels', makeAndModels)
    },

    async getMakesCar ({ commit }) {
      const list = await httpClient.get('/getbrands')
        .then(({ data }) => data.brands)
      const brands = list
        .sort((a, b) => b.popular - a.popular)
        .map(({ id, name, popular }) => ({
          name,
          id,
          popular
        }))
      return commit('setMakesCar', brands)
    },
    getCarUpgradeToOdSchemes ({ state, commit }, value) {
      const vehValue = value || 1000
      if (vehValue === state.motorUpgradeSchemes.value &&
        state.promocode === state.motorUpgradeSchemes.promocode) {
        return {
          success: true,
          ...state.motorUpgradeSchemes.info
        }
      }
      const policyInfo = this.state.renew.policyInfo
      const oneDay = 86400
      const policyEndDate = policyInfo.polEndDate && Math.floor(+policyInfo.polEndDate / 1000 + oneDay)
      const curDate = new Date()
      const defaultDob = policyInfo.driverDOB ? new Date(policyInfo.driverDOB) : new Date(1990, 0, 1)
      const dobToUnix = dateToUnix(defaultDob)
      const unixCurDate = dateToUnix(curDate)
      // let firstName = ''
      // let lastName = ''
      // if (policyInfo.insName) {
      //   const nameList = policyInfo.insName.split(' ').filter(n => !!n)
      //   firstName = nameList[0]
      //   lastName = nameList.slice(1).join(' ')
      // }
      return this.dispatch('sendFormData', {
        // phone: policyInfo?.mobileNo ?? '',
        // email: policyInfo?.emailId ?? '',
        // qatar_id: policyInfo?.civilId ?? '',
        // first_name: firstName ?? '',
        // last_name: lastName ?? '',
        birth_date: dobToUnix,
        policy_date: (policyInfo?.upgToODYN || policyInfo?.upgToOD) && policyEndDate ? policyEndDate : unixCurDate,
        brand: policyInfo?.makeCode ?? '',
        model: policyInfo?.modelCode ?? '',
        model_year: policyInfo?.modelYear ?? '',
        plate: policyInfo?.regNo ?? '',
        seats: policyInfo?.seatingCapacity ?? '',
        seatsname: policyInfo?.seatingCapacity ?? '',
        vehicle_type: policyInfo?.vehicleTypeCode ?? '',
        vehicletypename: policyInfo?.vehicleType ?? '',
        vehicle_shape_renew: policyInfo?.vehicleShape ?? '',
        vehicle_type_renew: policyInfo?.vehicleTypeCode ?? '',
        plate_type: policyInfo?.plateCharacter ?? '',
        cylindersname: policyInfo?.noOfCylinder ?? '',
        cylinders: policyInfo?.noOfCylinder ? 1000 + Number(policyInfo?.noOfCylinder) : '',
        year_registration: policyInfo?.regYear ?? '',
        chassis: policyInfo?.chassisNo ?? '',
        vehicle_value: vehValue ?? ''
      })
        .catch(() => {
          return Promise.resolve()
        })
        .then(() => {
          return this.dispatch('getMotorODQuote', { value: vehValue })
        })
    },

    getMotorODQuote ({ state, commit, dispatch }, { value }) {
      const getOdPrice = (promoCode, onSuccess) => {
        const params = {
          vehiclevalue: value
        }
        if (promoCode) {
          params.promocode = promoCode
        }
        return httpClient.get('getodprice', { params })
          .then((response) => {
            const odPriceResponse = {
              value,
              promocode: promoCode,
              info: {
                quoteno: response.data.quoteno,
                schemes: response.data.schemes.map(s => {
                  s.fullPrice = Number(s?.price) + Number(s?.discount)
                  return s
                })
              }
            }
            if (onSuccess) {
              onSuccess()
            }
            commit('setMotorSchemesUpgradeInfo', odPriceResponse)
            return {
              success: true,
              ...odPriceResponse.info
            }
          })
      }
      const withoutPromoCodes = () => {
        commit('setIsCampaignPromoCodeApplied', false)
        commit('setIsSuccessPromocode', false)
        return getOdPrice()
          .catch(() => {
            const defaultOdPriceResponse = {
              value,
              promocode: state.promocode,
              info: {
                quoteno: null,
                schemes: []
              }
            }
            commit('setMotorSchemesUpgradeInfo', defaultOdPriceResponse)
            return { success: false, ...defaultOdPriceResponse.info }
          })
      }
      const withPromoCode = () => {
        return getOdPrice(state.promocode, () => commit('setIsSuccessPromocode', true))
          .catch(() => {
            commit('setIsSuccessPromocode', false)
            return withoutPromoCodes()
          })
      }
      const withPromoCampaign = () => {
        return getOdPrice(state.promoCampaign, () => commit('setIsCampaignPromoCodeApplied', true))
          .catch(() => {
            commit('setIsCampaignPromoCodeApplied', false)
            return withPromoCode()
          })
      }
      const isPromoCampaign = Boolean(state.promoCampaign)
      const isPromoCode = Boolean(state.promocode)

      if (isPromoCampaign) {
        return withPromoCampaign()
      }
      if (isPromoCode) {
        return withPromoCode()
      }
      return withoutPromoCodes()
    },

    async getMotorRenewSchemes ({ state, commit, dispatch }, filterByProdCode) {
      const productCode = state.changePlan.status ? state.changePlan.prodCode : this.state.renew.policyInfo?.prodCode
      const requests = [dispatch('getMotorRenewPolicy')]
      const policyInfo = this.state.renew.policyInfo

      /** https://qicbizdev.atlassian.net/browse/QIC-2614 Turn off upgrade flow (for investigation) */
      // const upgToOD = policyInfo?.upgToODYN === '1' || policyInfo?.upgToOD
      // const isUpgrade = upgToOD || new Date(policyInfo?.polEndDate || '') < new Date()
      const isUpgrade = false

      commit('setIsUpgradePossible', isUpgrade)

      const vehicleValue = await dispatch('getCarValueXA', policyInfo?.chassisNo)
      commit('setIsCampaignPromoCodeApplied', false)
      if (policyInfo?.prodCode === TPL_PROD_CODE && isUpgrade) {
        let priceVehicle = 1000
        if (vehicleValue.vehicleValues) {
          priceVehicle = vehicleValue.vehicleValues.medium
        } else if (vehicleValue.minCarValue) {
          priceVehicle = vehicleValue.minCarValue
        }
        await dispatch('getPromoCampaign', priceVehicle)
        requests.push(dispatch('getCarUpgradeToOdSchemes', state.vehicleValue || priceVehicle))
      }
      return Promise.all(requests)
        .then(([policySchemes, odSchemes]) => {
          const policy = policySchemes.schemes
          const od = odSchemes ? odSchemes.schemes : []
          const schemes = [
            ...policy,
            ...od
          ]
          if (filterByProdCode) {
            return schemes.filter(s => s.productcode === productCode)
          }
          if (!state.isSuccessPromocode) {
            commit('setMotorPromocode', '')
          }
          return schemes
        })
    },

    getMotorRenewQuote ({ state, dispatch, commit }) {
      const price = state.renewScheme.price
      const requests = [dispatch('getMotorRenewPolicy')]
      const prodCode = this.state.renew.policyInfo?.prodCode
      if (prodCode === TPL_PROD_CODE && state.changePlan.status) {
        /** https://qicbizdev.atlassian.net/browse/QIC-2614 Turn off upgrade flow (for investigation) */
        // requests.push(dispatch('getCarUpgradeToOdSchemes', state.vehicleValue))
      }
      return Promise.all(requests)
        .then(([policy, od]) => {
          commit('setMotorQuoteNumber', policy.quoteno)
          if (od && od?.schemes && od.schemes.length) {
            return {
              quoteNumber: prodCode === TPL_PROD_CODE && state.changePlan.status ? od.quoteno : policy.quoteno,
              scheme: [
                ...od.schemes,
                ...policy.schemes
              ].find(s => s.price === price)
            }
          }
          return {
            quoteNumber: policy.quoteno,
            scheme: policy.schemes.find(s => s.price === price)
          }
        })
    },

    getMotorRenewPolicy ({ state, dispatch }) {
      const policyInfo = this.state.renew.policyInfo
      const promocode = state.promocode
      if (!policyInfo) throw new Error('Choose policy first')

      return dispatch('getMotorPolicyRenew')
        .catch((err) => {
          if (err.message === '1001' || err.message === '1002') {
            throw err
          }
          if (promocode) {
            return dispatch('getMotorPolicyRenew', false)
          }
          throw (typeof err === 'string') ? new Error(err) : err
        })
        .then((info) => {
          return info
        })
    },

    getMotorPolicyRenew ({ state, commit, dispatch }, applyWithPromocode = true) {
      const policyInfo = this.state.renew.policyInfo
      const qid = this.state.renew.userData.qid
      const transId = this.state.renew.userData.transId
      const promocode = state.promocode
      const params = {
        policyNo: policyInfo?.policyNo,
        transId
      }
      if (promocode && applyWithPromocode) {
        params.promocode = promocode
      }

      /** https://qicbizdev.atlassian.net/browse/QIC-2615 Turn off upgrade flow (for investigation) */
      // const date = new Date(state.dateOfBirth)
      //

      let driverDOB = new Date(1990, 0, 1)

      if (qid && qid.length > 2) {
        const centuryIndicator = qid.charAt(0)
        const yearInCentury = parseInt(qid.substring(1, 3))

        let fullYear
        if (centuryIndicator === '2') {
          fullYear = 1900 + yearInCentury
        } else if (centuryIndicator === '3') {
          fullYear = 2000 + yearInCentury
        }

        if (fullYear) {
          driverDOB = new Date(fullYear, 0, 1)
        }
      }

      const day = String(driverDOB.getDate()).padStart(2, '0')
      const month = String(driverDOB.getMonth() + 1).padStart(2, '0')
      const year = driverDOB.getFullYear()

      params.driverDOB = `${day}/${month}/${year}`
      params.civilId = qid

      return httpClient.get('/policyrenewal', { params })
        .then(response => {
          if (params.promocode && response.data.success) {
            commit('setIsSuccessPromocode', true)
          }

          commit('setMotorPolicySchemesInfo', {
            policyNumber: policyInfo?.policyNo,
            promocode,
            info: {
              quoteno: response.data.quoteno,
              schemes: response.data.schemes.map(s => {
                s.fullPrice = Number(s?.price) + Number(s?.discount)
                return s
              }) || []
            }
          })
          return response.data
        })
    },

    getMotorRenewPrice ({ state, dispatch }, data) {
      data.productcode = state.renewScheme.productcode
      data.schemecode = state.renewScheme.schemecode
      data.quoteno = state.quoteNumber
      if (state.changePlan.status && state.changePlan.prodCode === '0110' && state.motorUpgradeSchemes.info.schemes) {
        data.quoteno = state.motorUpgradeSchemes.info.quoteno
      }
      return this.dispatch('getTotalRenewPrice', { data, method: 'gettotalprice' })
    },

    redirectMotorToRenewPayment ({ state, dispatch }, { paymentMethod, paymentPlan, ids, deductibles }) {
      return dispatch('getMotorRenewPrice', {
        ids,
        selectproduct: true,
        paymentmethod: paymentMethod,
        ...deductibles
      })
        .then(() => {
          let link = paymentMethod === 0 ? process.env.VUE_APP_REDIRECT_CC : process.env.VUE_APP_REDIRECT_DC
          if (paymentPlan === 'monthly') {
            link = process.env.VUE_APP_REDIRECT_CC_MONTHLY + '?action=installment'
          }
          window.location.href = link
        })
    },

    getMotorPlateTypesWithParams ({ commit, state }, { vehicleType, vehicleUsage }) {
      return httpClient.get(`getlists/platetypes?vehicleType=${vehicleType}&vehicleUsage=${vehicleUsage}`)
        .then(data => {
          return data.data.list
        })
    },

    getMotorPlateTypes ({ commit, state }) {
      if (state.carLists.plateList) {
        return state.carLists.plateList
      }
      return httpClient.get('getlists/platetypes')
        .then(data => {
          commit('setCarPlateList', data.data.list)
          return data.data.list
        })
    },

    getMotorShapesDependOnVehicle ({ commit, state }, vehicleType) {
      return httpClient.get(`getlists/vehicleshapes?vehicletype=${vehicleType}`)
        .then(data => {
          // commit('setCarPlateList', data.data.list)
          return data.data.list
        })
    },

    setMotorDob ({ commit, state }, { dateOfBirth, qid }) {
      const tzOffset = dateOfBirth.getTimezoneOffset() * 60000
      const date = (dateOfBirth.getTime() - tzOffset) / 1000

      const params = {
        birth_date: date,
        qatar_id: qid,
        customer_type: qid?.length === 8 ? customerTypeMap.COMPANY : customerTypeMap.INDIVIDUAL
      }
      return this.dispatch('sendFormData', params)
        .then(() => {
          commit('setMotorDob', dateOfBirth)
          return true
        })
    },

    async updateMotorPolicy ({ dispatch, state }, quoteNumber) {
      await dispatch('sendFormDataWithInfo')
      return await dispatch('updateMotorInfo', quoteNumber)
    }
  }
}
